import React, { useState, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Provider } from 'react-redux';
import { configureStore } from '@reduxjs/toolkit';
import LaunchPage from './components/LaunchPage';
import AgentsDashboard from './pages/AgentsDashboard';
import MainLayout from './components/MainLayout';
import Login from './components/Login';
import Register from './components/Register';
import ProtectedRoute from './components/ProtectedRoute';
import AuthLoader from './components/AuthLoader';
import SyncSession from './components/SyncSession';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { IncubationStudioSession } from './components/IncubationStudioSession';
import ImagePromptIterator from './components/ImagePromptIterator';
import VideoPromptIterator from './components/VideoPromptIterator';
import ImageGenerationPage from './pages/ImageGeneration';
import SocialMediaIterator from './components/SocialMediaIterator';
import PromptImprovement from './components/PromptImprovement';
import ScreenplayGenerator from './components/ScreenplayGenerator';
import MusicPromptIterator from './components/MusicPromptIterator';
import NicheAgentsGallery from './pages/NicheAgentsGallery';
import NicheAgentIterator from './components/NicheAgentIterator';
import UXDesigner from './components/UXDesigner';
import CodingAgent from './components/CodingAgent';
import StoryGenerator from './components/StoryGenerator';
import UrbanAgentsGallery from './pages/UrbanAgentsGallery';
import UrbanAgentIterator from './components/UrbanAgentIterator';
import BlogMultiAgent from './components/BlogMultiAgent';
import TechDocMultiAgent from './components/TechDocMultiAgent';
import FashionMultiAgent from './components/FashionMultiAgent';
import FashionDesignStudio from './components/FashionDesignStudio';
import MediaGallery from './components/MediaGallery';
import JournalismMultiAgent from './components/JournalismMultiAgent';
import ComicBookAgent from './components/ComicBookAgent';
import InteractiveFictionAgent from './components/InteractiveFictionAgent';
import NovelWritingAgent from './components/NovelWritingAgent';
import AbstractThinker from './components/AbstractThinker';
import Chat from './components/Chat';
import ImageAnalysis from './components/ImageAnalysis';
import NewNicheAgentIterator from './components/NewNicheAgentIterator';
import ContentPackStudio from './components/ContentPackStudio';
import DataAnalysisStudio from './components/DataAnalysisStudio';
import { SimpleDataAgent } from './components/SimpleDataAgent';
import { RagTagSystem } from './components/RagTagSystem';
import { CharacterAgent } from './components/CharacterAgent';
import { SceneAgent } from './components/SceneAgent';
import { StoryboardAgent } from './components/StoryboardAgent';
import { ScientificAgent } from './components/ScientificAgent';
import { PaleontologyAgent } from './components/PaleontologyAgent';
import { CreativeProjectStudio } from './components/CreativeProjectStudio';
import { PromptEvolutionStudio } from './components/PromptEvolutionStudio';
// import { ResearchIntelligenceStudio } from './components/ResearchIntelligenceStudio';
import { CuisinePromptStudio } from './components/CuisinePromptStudio';
import { StoryPromptStudio } from './components/StoryPromptStudio';
// Individual cuisine agents hidden - using Cuisine Prompt Studio instead
// import {
//   ItalianCuisineAgent,
//   MexicanCuisineAgent,
//   IndianCuisineAgent,
//   ThaiCuisineAgent,
//   JapaneseCuisineAgent,
//   FrenchCuisineAgent,
// } from './components/cuisine';
import { theme, lightTheme } from './theme';
import IframeSyncHandler from './IframeSyncHandler';

// Configure Redux store (placeholder for now)
const store = configureStore({
  reducer: {
    // Add reducers here
  },
});

// App Content Component with Auth Integration
const AppContent: React.FC<{ isDarkMode: boolean; toggleTheme: () => void }> = ({
  isDarkMode,
  toggleTheme
}) => {
  const { isAuthReady, setAuthReady } = useAuth();
  const location = useLocation();

  const handleAuthReady = () => {
    console.log('Auth ready callback triggered');
    setAuthReady(true);
  };

  // Allow public routes and sync-session to render without auth check
  const isPublicRoute = location.pathname === '/login' ||
                        location.pathname === '/register' ||
                        location.pathname === '/sync-session';

  // Show loading screen until auth is ready (but not for public routes)
  if (!isAuthReady && !isPublicRoute) {
    return (
      <>
        <AuthLoader message="Initializing application..." />
        <IframeSyncHandler
          onAuthReady={handleAuthReady}
          isBlocking={true}
        />
      </>
    );
  }

  return (
    <>
      <IframeSyncHandler />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />

        {/* Special sync-session route for SSO callback */}
        <Route path="/sync-session" element={<SyncSession />} />
        
        {/* Protected routes */}
        <Route element={
          <ProtectedRoute>
            <MainLayout toggleTheme={toggleTheme} isDarkMode={isDarkMode} />
          </ProtectedRoute>
        }>
          <Route path="/" element={<Navigate to="/agents" replace />} />
          <Route path="/agents" element={<AgentsDashboard />} />
          <Route path="/incubation-studio" element={<IncubationStudioSession />} />
          <Route path="/image-prompt-iterator" element={<ImagePromptIterator />} />
          <Route path="/image-generation" element={<ImageGenerationPage />} />
          <Route path="/video-prompt-iterator" element={<VideoPromptIterator />} />
          <Route path="/social-media-studio" element={<SocialMediaIterator />} />
          {/* Placeholder routes for components not yet implemented */}
          <Route path="/code-improvement" element={<CodingAgent />} />
          <Route path="/prompt-improvement" element={<PromptImprovement />} />
          <Route path="/comic-book-creator" element={<ComicBookAgent />} />
          <Route path="/screenplay-generator" element={<ScreenplayGenerator />} />
          <Route path="/chat" element={<Chat />} />
          <Route path="/story-generator" element={<StoryGenerator />} />
          <Route path="/music-prompt-iterator" element={<MusicPromptIterator />} />
          <Route path="/niche-agents" element={<NicheAgentsGallery />} />
          <Route path="/niche-agents/:agentType" element={<NicheAgentIterator />} />
          <Route path="/new-niche-agents/:agentId" element={<NewNicheAgentIterator />} />
          <Route path="/ux-designer" element={<UXDesigner />} />
          <Route path="/urban-agents" element={<UrbanAgentsGallery />} />
          <Route path="/urban-agents/:agentType" element={<UrbanAgentIterator />} />
          <Route path="/blog-multi-agent" element={<BlogMultiAgent />} />
          <Route path="/tech-doc-multi-agent" element={<TechDocMultiAgent />} />
          <Route path="/fashion-multi-agent" element={<FashionMultiAgent />} />
          <Route path="/fashion-design-studio" element={<FashionDesignStudio />} />
          <Route path="/media" element={<MediaGallery />} />
          <Route path="/journalism-multi-agent" element={<JournalismMultiAgent />} />
          <Route path="/interactive-fiction" element={<InteractiveFictionAgent />} />
          <Route path="/novel-writing" element={<NovelWritingAgent />} />
          <Route path="/abstract-thinker" element={<AbstractThinker />} />
          <Route path="/image-analysis" element={<ImageAnalysis />} />
          <Route path="/content-pack-studio" element={<ContentPackStudio />} />
          <Route path="/data-analysis-studio" element={<DataAnalysisStudio />} />
          <Route path="/simple-data-agent" element={<SimpleDataAgent />} />
          <Route path="/ragtag-system" element={<RagTagSystem />} />
          <Route path="/character-agent" element={<CharacterAgent />} />
          <Route path="/scene-agent" element={<SceneAgent />} />
          <Route path="/storyboard-agent" element={<StoryboardAgent />} />
          <Route path="/scientific-agent" element={<ScientificAgent />} />
          <Route path="/paleontology-agent" element={<PaleontologyAgent />} />
          <Route path="/creative-studio" element={<CreativeProjectStudio />} />
          <Route path="/prompt-evolution" element={<PromptEvolutionStudio />} />
          {/* <Route path="/research-studio" element={<ResearchIntelligenceStudio />} /> */}
          <Route path="/cuisine-prompt-studio" element={<CuisinePromptStudio />} />
          <Route path="/story-prompt-studio" element={<StoryPromptStudio />} />
          {/* Cuisine Agent Routes - Hidden, using Cuisine Prompt Studio instead */}
          {/* <Route path="/italian-cuisine" element={<ItalianCuisineAgent />} />
          <Route path="/mexican-cuisine" element={<MexicanCuisineAgent />} />
          <Route path="/indian-cuisine" element={<IndianCuisineAgent />} />
          <Route path="/thai-cuisine" element={<ThaiCuisineAgent />} />
          <Route path="/japanese-cuisine" element={<JapaneseCuisineAgent />} />
          <Route path="/french-cuisine" element={<FrenchCuisineAgent />} /> */}
        </Route>
      </Routes>
    </>
  );
};

function App() {
  const [isDarkMode, setIsDarkMode] = useState(true);
  
  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };
  
  const activeTheme = useMemo(() => isDarkMode ? theme : lightTheme, [isDarkMode]);

  return (
    <Provider store={store}>
      <ThemeProvider theme={activeTheme}>
        <CssBaseline />
        <Router>
          <AuthProvider>
            <AppContent isDarkMode={isDarkMode} toggleTheme={toggleTheme} />
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </Provider>
  );
}

export default App;